import {
  CardNumberChallenge,
  CardNumberChallengeItem,
  CardNumberDecline,
  CardNumberError,
  CardNumberNotEligible,
  CardNumberResult,
  CardNumberResultTypenames,
  CardNumberSuccess,
  Mutation,
  Query,
  ReplaceCardChatHoursResult,
  SetCardLockedFailure,
  SetCardLockedResultTypenames,
} from '@varo/graphql-schema-types';
import { CardExpirationStatusEnum } from 'features/cardManagement/helpers/CardExpirationStatusEnum';
import { isCardAlreadyExpired } from 'features/cardManagement/helpers/cardExpirationValidation.helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SetCardLocked from 'services/graphql/mutations/SetCardLocked';
import FetchCardCvv from 'services/graphql/queries/FetchCardCVV';
import FetchReplaceCardChatHours from 'services/graphql/queries/FetchReplaceCardChatHours';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useRefreshSystemBanners } from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import { getCardCvvData, saveCardCvvData } from 'shared/utils/getCvv';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { useQuery } from 'urql';

import useFetchCardAndUserDetails from './useFetchCardAndUserDetails';

interface LockCardError {
  title: string;
  message: string;
}

const parseCachedCvv = (cachedData: string | null): string | undefined => {
  if (!cachedData) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(cachedData);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'cvv2' in parsed &&
      typeof parsed.cvv2 === 'string'
    ) {
      return parsed.cvv2;
    }
  } catch {
    // Corrupt or unexpected cache data — treat as cache miss
    return undefined;
  }
};

const isCardNumberSuccess = (r: CardNumberResult): r is CardNumberSuccess =>
  r.__typename === CardNumberResultTypenames.CardNumberSuccess;

const isCardNumberChallenge = (r: CardNumberResult): r is CardNumberChallenge =>
  r.__typename === CardNumberResultTypenames.CardNumberChallenge;

const isCardNumberDecline = (r: CardNumberResult): r is CardNumberDecline =>
  r.__typename === CardNumberResultTypenames.CardNumberDecline;

const isCardNumberErrorResult = (
  r: CardNumberResult,
): r is CardNumberError | CardNumberNotEligible =>
  r.__typename === CardNumberResultTypenames.CardNumberError ||
  r.__typename === CardNumberResultTypenames.CardNumberNotEligible;

const isSetCardLockedFailure = (
  result: Mutation['setCardLocked'] | null | undefined,
): result is SetCardLockedFailure =>
  result?.__typename === SetCardLockedResultTypenames.SetCardLockedFailure;

export const useManageCard = ({
  cardId,
  onChallenge,
  onDeclined,
}: {
  cardId: string;
  onChallenge?: (challenges: CardNumberChallengeItem[]) => void;
  onDeclined?: () => void;
}) => {
  const { t: translatedText } = useTranslation('cardManagement', {
    keyPrefix: 'manageCard',
  });

  const [cardCVVResponse, setCardCVVResponse] = useState<{
    data: CardNumberSuccess | undefined;
    error: CardNumberError | CardNumberNotEligible | null;
  }>({ data: undefined, error: null });

  const onChallengeRef = useRef(onChallenge);
  const onDeclinedRef = useRef(onDeclined);
  useEffect(() => {
    onChallengeRef.current = onChallenge;
    onDeclinedRef.current = onDeclined;
  }, [onChallenge, onDeclined]);

  const [errorData, setErrorData] = useState<LockCardError | null>(null);
  const { refreshSystemBanners, fetching: refreshingSystemBanners } =
    useRefreshSystemBanners();

  const [cvvFetchParams, setCvvFetchParams] = useState<{
    input: { cardId: string; challengeId?: string };
    includeCvv: boolean;
  } | null>(null);
  const cachedCvv2Ref = useRef<string | undefined>(undefined);

  const [{ data: cvvData, fetching: fetchingCVV, error: fetchCardCVVError }] =
    useQuery<Query>({
      query: FetchCardCvv,
      variables: cvvFetchParams ?? { input: { cardId }, includeCvv: true },
      pause: !cvvFetchParams,
      requestPolicy: 'network-only',
    });

  const {
    cardDetails,
    userDetails,
    cardExpiration,
    fetching: fetchingCardAndUser,
    error: fetchCardAndUserError,
  } = useFetchCardAndUserDetails({ cardId });

  const [
    {
      data: replaceCardChatHoursData,
      fetching: fetchingReplaceCardChatHours,
      error: fetchReplaceCardChatHoursError,
    },
    getReplaceCardChatHours,
  ] = useQuery<Query, ReplaceCardChatHoursResult>({
    query: FetchReplaceCardChatHours,
    pause: true,
    requestPolicy: 'cache-first',
  });

  const [
    {
      fetching: fetchingLockCardMutation,
      error: lockCardMutationError,
      data: lockCardMutationData,
    },
    executeLockCardMutation,
  ] = useCancellableMutation<Mutation>(SetCardLocked);

  const setCardLockedFailureData = isSetCardLockedFailure(
    lockCardMutationData?.setCardLocked,
  )
    ? lockCardMutationData.setCardLocked
    : null;

  useEffect(() => {
    if (lockCardMutationError || setCardLockedFailureData) {
      setErrorData({
        title: setCardLockedFailureData?.title ?? translatedText('error.title'),
        message:
          setCardLockedFailureData?.message ?? translatedText('error.message'),
      });
    }
  }, [lockCardMutationError, translatedText, setCardLockedFailureData]);

  const handleCardNumberShownAnalytics = usePressableAnalytics({
    analyticsId: 'cardCredentialCardNumberShown',
  });
  const handleCardNumberShownAnalyticsRef = useRef(
    handleCardNumberShownAnalytics,
  );
  useEffect(() => {
    handleCardNumberShownAnalyticsRef.current = handleCardNumberShownAnalytics;
  }, [handleCardNumberShownAnalytics]);

  const processCvvData = useCallback(
    async (cardNumber: CardNumberResult | undefined) => {
      const clearCredentialData = () => {
        setCardCVVResponse({ data: undefined, error: null });
      };

      const handleCVVError = (
        error?: CardNumberError | CardNumberNotEligible,
      ) => {
        setCardCVVResponse({
          data: undefined,
          error: error || {
            __typename: 'CardNumberError',
            code: 'CARD_NUMBER_ERROR',
            title: translatedText('error.title'),
            message: translatedText('error.message'),
          },
        });
      };

      if (!cardNumber) {
        handleCVVError();
        return;
      }
      if (isCardNumberSuccess(cardNumber)) {
        const { pan, expiration, cvv2: networkCvv2 } = cardNumber;
        const cvv2 = networkCvv2 ?? cachedCvv2Ref.current;
        const fullCardNumber: CardNumberSuccess = {
          __typename: 'CardNumberSuccess',
          pan,
          expiration,
          cvv2: cvv2 ?? '',
        };
        setCardCVVResponse({ data: fullCardNumber, error: null });
        handleCardNumberShownAnalyticsRef.current();
        if (cvv2) {
          await saveCardCvvData(
            cardId,
            JSON.stringify({
              __typename: CardNumberResultTypenames.CardNumberSuccess,
              cvv2,
            }),
          );
        }
      } else if (isCardNumberChallenge(cardNumber)) {
        clearCredentialData();

        if (!cardNumber.challenges?.length) {
          handleCVVError();
          return;
        }

        onChallengeRef.current?.(cardNumber.challenges);
      } else if (isCardNumberDecline(cardNumber)) {
        clearCredentialData();
        onDeclinedRef.current?.();
      } else if (isCardNumberErrorResult(cardNumber)) {
        handleCVVError(cardNumber);
      } else {
        handleCVVError();
      }
    },
    [cardId, translatedText],
  );

  useEffect(() => {
    if (cvvData?.cardNumber) {
      setCvvFetchParams(null);
      void processCvvData(cvvData.cardNumber);
    }
  }, [cvvData, processCvvData]);

  const handleViewCardButtonAnalytics = usePressableAnalytics({
    analyticsId: 'viewCardNumber',
    customAttributes: {
      card_type: cardDetails?.type?.toLowerCase(),
    },
  });

  const handleViewCardInitiatedAnalytics = usePressableAnalytics({
    analyticsId: 'cardCredentialViewCardInitiated',
  });

  const handleViewCardNumberStartedAnalytics = usePressableAnalytics({
    analyticsId: 'cardCredentialViewCardNumberStarted',
  });

  const getCvvDetails = useCallback(
    async (challengeId?: string) => {
      if (isWeb) {
        return;
      }
      handleViewCardButtonAnalytics();
      handleViewCardNumberStartedAnalytics();
      if (!challengeId) {
        handleViewCardInitiatedAnalytics();
      }

      const cachedData = await getCardCvvData(cardId);
      const cachedCvv = parseCachedCvv(cachedData);
      cachedCvv2Ref.current = cachedCvv;

      setCvvFetchParams({
        input: { cardId, ...(challengeId && { challengeId }) },
        includeCvv: !cachedCvv,
      });
    },
    [
      handleViewCardButtonAnalytics,
      handleViewCardInitiatedAnalytics,
      handleViewCardNumberStartedAnalytics,
      cardId,
    ],
  );

  const handleLockCard = async (locked: boolean) => {
    try {
      const response = await executeLockCardMutation({
        idempotencyId: getUuid(),
        cardId,
        locked,
      });
      const SetCardLockedData = response?.data?.setCardLocked;
      if (
        SetCardLockedData?.__typename ===
        SetCardLockedResultTypenames.SetCardLockedSuccess
      ) {
        refreshSystemBanners();
      } else if (isSetCardLockedFailure(SetCardLockedData)) {
        setErrorData(SetCardLockedData);
      } else {
        setErrorData({
          title: translatedText('error.title'),
          message: translatedText('error.message'),
        });
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
      setErrorData({
        title: translatedText('error.title'),
        message: translatedText('error.message'),
      });
    }
  };

  let cardExpiryStatus: CardExpirationStatusEnum =
    CardExpirationStatusEnum.Unknown;
  if (
    cardExpiration?.__typename === CardNumberResultTypenames.CardNumberSuccess
  ) {
    cardExpiryStatus = isCardAlreadyExpired(cardExpiration.expiration);
  }

  return {
    fetchingLockCardMutation:
      fetchingLockCardMutation || refreshingSystemBanners,
    handleLockCard,
    errorData,
    cardDetails,
    userDetails,
    cardExpiryStatus,
    fetchingCardAndUser,
    fetchCardAndUserError,
    cardCVVResponse,
    fetchingCVV,
    fetchCardCVVError,
    getCvvDetails,
    replaceCardData: replaceCardChatHoursData?.replaceCardChatHours,
    fetchingReplaceCardChatHours,
    fetchReplaceCardChatHoursError,
    getReplaceCardChatHours,
  };
};

export default useManageCard;
